import React, { useState, useEffect, useContext } from 'react';
import ToggleButton from '../components/ToggleButton';
import { PanoramaVFTConfig, PanoramaVFTInfospotConfig, PanoramaVFTPortalConfig, PanoramaVFTSceneConfig } from '../models/PanoramaVFTConfig';
import * as Tabs from "@radix-ui/react-tabs";
import SelectDropdown from '../components/SelectDropdown';
import NodeEditorHeader from '../components/NodeEditorHeader';
import PanoramaVFTSceneEditor from './PanoramaVFTSceneEditor';
import { shiftElementUp, shiftElementDown, deleteArrayIndex, getFileListFromFiles } from '../lib/Helper';
import PanoramaVFTPortalEditor from './PanoramaVFTPortalEditor';
import PanoramaVFTInfospotEditor from './PanoramaVFTInfospotEditor';
import { Upload } from 'lucide-react';
import { SettingsContext } from '../frames/Main';
import { useToast } from '../components/ToastContext';
import { useDropzone } from 'react-dropzone';

type PanoramaVFTEditorProps = {
    globalConfig: PanoramaVFTConfig;
    updateConfig: (newConfig: PanoramaVFTConfig) => void;
    canvasContext: any
};

function PanoramaVFTEditor({ globalConfig, updateConfig, canvasContext }: PanoramaVFTEditorProps) {

    const [htmlPaths, setHtmlPaths] = useState<Map<string, string>>(new Map<string, string>());
    const [stagedFiles, setStagedFiles] = useState<FileList | null>(null);
    const { setIsToastOpen, setToastMessage, setToastTitle } = useToast();
    const { HTML_URL, IMAGE_URL } = useContext(SettingsContext);
    const canvasDisplayModes = new Map<string, string>([["split", "Split view"], ["overlay", "Overlay view"]]);
    const canvasOverlayBackgrounds = new Map<string, string>([["#ffffff", "White"], ["transparent", "Transparent"], ["#000000", "Black"]]);

    useEffect(() => {
        (async function () {
            try {
                let loadedPaths = await fetch(HTML_URL, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }).then((response) => {
                    return response.json();
                });

                const mappedPaths = new Map<string, string>(
                    loadedPaths.map((value: string, index: number) => [value, value])
                );

                setHtmlPaths(mappedPaths);
            } catch { }
        })();
    }, [HTML_URL]);

    const onDrop = (acceptedFiles: File[]) => {
        setStagedFiles(getFileListFromFiles(acceptedFiles, stagedFiles, ['image/svg+xml', 'image/png', 'image/jpeg', 'image/gif']));
    }

    const { getRootProps, isDragActive } = useDropzone({ onDrop });

    const onFilesStaged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStagedFiles(e.target.files);
    }

    const onImageSave = async () => {
        const formData = new FormData();

        if (!stagedFiles) {
            setToastTitle("Error");
            setToastMessage("No files staged.");
            setIsToastOpen(true);
            return;
        }

        for (let i = 0; i < stagedFiles.length; i++) {
            formData.append('files', stagedFiles[i]);
        }

        try {
            const response = await fetch(IMAGE_URL, {
                method: "POST",
                body: formData
            })

            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Upload completed.");
                setStagedFiles(null);
            } else {
                setToastTitle("Error");
                var res = await response.json();
                setToastMessage(res.message);
            }
        } catch {
            setToastTitle("Error");
            setToastMessage("Upload unsuccessful. Please try again.")
        }
        setIsToastOpen(true);
    }

    const onImageClear = () => {
        setStagedFiles(null);
    }

    const onBaseScaleChange = (e: React.FormEvent<HTMLInputElement>) => {
        updateConfig({
            ...globalConfig,
            baseScale: Number(e.currentTarget.value)
        });
    }

    const onOffsetChange = (e: React.FormEvent<HTMLInputElement>) => {
        updateConfig({
            ...globalConfig,
            offset: Number(e.currentTarget.value)
        });
    }

    const onFovChange = (e: React.FormEvent<HTMLInputElement>) => {
        updateConfig({
            ...globalConfig,
            fov: Number(e.currentTarget.value)
        });
    }

    const onOnloadPortalChange = (newValue: string) => {
        updateConfig({
            ...globalConfig,
            onloadPortal: newValue
        });
    }

    const onAutoRotateChange = () => {
        updateConfig({
            ...globalConfig,
            autoRotate: !globalConfig.autoRotate
        });
    }

    const onCanvasDisplayChange = (displayMode: string) => {
        updateConfig({
            ...globalConfig,
            canvasDisplayMode: displayMode
        });
    }

    const onCanvasSrcChange = (newValue: string) => {
        updateConfig({
            ...globalConfig,
            canvasSrc: newValue
        });
    }

    const onCanvasOverlayWidthChange = (e: React.FormEvent<HTMLInputElement>) => {
        updateConfig({
            ...globalConfig,
            canvasOverlayWidth: Number(e.currentTarget.value)
        });
    }

    const onCanvasOverlayHeightChange = (e: React.FormEvent<HTMLInputElement>) => {
        updateConfig({
            ...globalConfig,
            canvasOverlayHeight: Number(e.currentTarget.value)
        });
    }

    const onCanvasOverlayBackgroundChange = (background: string) => {
        updateConfig({
            ...globalConfig,
            canvasOverlayBackground: background
        });
    }

    const onSceneAdd = () => {
        globalConfig.scenes.push({
            eventName: "",
            eventType: "scene",
            sourceAsset: "./images/blank.png"
        });
        updateConfig({
            ...globalConfig,
            scenes: globalConfig.scenes
        });
    }

    const onSceneChange = (s: PanoramaVFTSceneConfig, key: number) => {
        globalConfig.scenes[key] = s;
        updateConfig({
            ...globalConfig,
            scenes: globalConfig.scenes
        });
    }

    const onSceneEditorUp = (key: number) => {
        updateConfig({
            ...globalConfig,
            scenes: shiftElementUp(key, globalConfig.scenes)
        });
    }

    const onSceneEditorDown = (key: number) => {
        updateConfig({
            ...globalConfig,
            scenes: shiftElementDown(key, globalConfig.scenes)
        });
    }

    const onSceneEditorDelete = (key: number) => {
        updateConfig({
            ...globalConfig,
            scenes: deleteArrayIndex(key, globalConfig.scenes)
        });
    }

    const onPortalAdd = () => {
        globalConfig.vftPortals.push({
            eventName: "",
            eventType: "vftPortal",
            degree: 0,
            target: globalConfig.scenes.length ? globalConfig.scenes[0].eventName : ""
        });
        updateConfig({
            ...globalConfig,
            vftPortals: globalConfig.vftPortals
        });
    }

    const onPortalChange = (p: PanoramaVFTPortalConfig, key: number) => {
        globalConfig.vftPortals[key] = p;
        updateConfig({
            ...globalConfig,
            vftPortals: globalConfig.vftPortals
        });
    }

    const onPortalEditorUp = (key: number) => {
        updateConfig({
            ...globalConfig,
            vftPortals: shiftElementUp(key, globalConfig.vftPortals)
        });
    }

    const onPortalEditorDown = (key: number) => {
        updateConfig({
            ...globalConfig,
            vftPortals: shiftElementDown(key, globalConfig.vftPortals)
        });
    }

    const onPortalEditorDelete = (key: number) => {
        updateConfig({
            ...globalConfig,
            vftPortals: deleteArrayIndex(key, globalConfig.vftPortals)
        });
    }

    const onInfospotEditorAdd = () => {
        globalConfig.infoSpots.push({
            eventName: "",
            eventType: "hotspot",
            scene: globalConfig.scenes.length ? globalConfig.scenes[0].eventName : "",
            sourceAsset: "./images/uns.png",
            selectedAsset: "./images/sel.png",
            visitedAsset: "./images/vis.png",
            infoText: "Tooltip",
            x: 0,
            y: 0,
            z: 0,
            target: ""
        });
        updateConfig({
            ...globalConfig,
            infoSpots: globalConfig.infoSpots
        });
    }

    const onInfospotChange = (i: PanoramaVFTInfospotConfig, key: number) => {
        globalConfig.infoSpots[key] = i;
        updateConfig({
            ...globalConfig,
            infoSpots: globalConfig.infoSpots
        });
    }

    const onInfospotEditorUp = (key: number) => {
        updateConfig({
            ...globalConfig,
            infoSpots: shiftElementUp(key, globalConfig.infoSpots)
        });
    }

    const onInfospotEditorDown = (key: number) => {
        updateConfig({
            ...globalConfig,
            infoSpots: shiftElementDown(key, globalConfig.infoSpots)
        });
    }

    const onInfospotEditorDelete = (key: number) => {
        updateConfig({
            ...globalConfig,
            infoSpots: deleteArrayIndex(key, globalConfig.infoSpots)
        });
    }

    const tabTriggerClasses = "h-[40px] cursor-pointer select-none items-center justify-center bg-white px-5 text-[14px] leading-none outline-none text-gray-700 " +
        "hover:bg-primary-gray data-[state=active]:shadow-[inset_0_-1px_0_0,0_1px_0_0] data-[state=active]:shadow-primary-blue";

    const labelClasses = "block text-left tracking-wide text-gray-700 text-xs font-bold mb-2";

    const inputClasses = "appearance-none block w-full text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500";

    const mappedScenes = new Map<string, string>(globalConfig.scenes.map((value, index) => [value.eventName, value.eventName]));
    const mappedVftPortals = new Map<string, string>(
        Object.keys(canvasContext?.registeredPortals ?? {}).map((value, index) => [value, value])
    );

    return (
        <div className='container mx-auto my-3'>
            <div className='p-6 bg-white border border-gray-200 rounded-lg shadow'>
                <form>
                    <Tabs.Root className="flex flex-col" defaultValue="panorama">
                        <Tabs.List className="flex shrink-0 border-b border-mauve6">
                            <Tabs.Trigger className={tabTriggerClasses} value="image">Images</Tabs.Trigger>
                            <Tabs.Trigger className={tabTriggerClasses} value="panorama">Panorama</Tabs.Trigger>
                            <Tabs.Trigger className={tabTriggerClasses} value="scene">Scenes</Tabs.Trigger>
                            <Tabs.Trigger className={tabTriggerClasses} value="portal">Portals</Tabs.Trigger>
                            <Tabs.Trigger className={tabTriggerClasses} value="infospot">Infospots</Tabs.Trigger>
                        </Tabs.List>

                        <div className='my-3'>
                            <Tabs.Content value="image">
                                <div className='my-3 w-full'>
                                    {
                                        (stagedFiles && stagedFiles.length > 0)
                                        &&
                                        <div className="w-full py-3 flex justify-between">
                                            <span>Staged files: {Array.from(stagedFiles).map(file => file.name).join(", ")}</span>
                                            <button
                                                type="button"
                                                className="text-white bg-primary-blue hover:bg-blue-800 focus:outline-none font-medium rounded-lg text-sm px-2.5 py-2 text-center"
                                                onClick={onImageClear}>Clear staged files
                                            </button>
                                        </div>
                                    }

                                    <div className="w-full py-3">
                                        <div {...getRootProps()} className="flex items-center justify-center w-full">
                                            <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50">
                                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                    <Upload className='mb-2' />
                                                    {
                                                        isDragActive
                                                            ? <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Drop the files here</span></p>
                                                            : <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                                                    }
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">SVG, PNG, JPG, or GIF</p>
                                                </div>
                                                <input type="file" className="hidden" accept="image/*" multiple onChange={onFilesStaged} />
                                            </label>
                                        </div>
                                    </div>
                                    <div className="w-full py-3">
                                        <button
                                            type="button"
                                            className="w-full text-white bg-primary-blue hover:bg-blue-800 focus:outline-none font-medium rounded-lg text-sm px-2.5 py-2 text-center me-2"
                                            onClick={onImageSave}>Upload images
                                        </button>
                                    </div>
                                </div>
                            </Tabs.Content>

                            <Tabs.Content value="panorama">
                                <div className="flex flex-wrap -mx-3 mb-4">
                                    <div className="w-full md:w-1/3 px-3">
                                        <label className={labelClasses}>Base scale</label>
                                        <input className={inputClasses} type="number" placeholder="800" value={globalConfig.baseScale} onChange={onBaseScaleChange} />
                                    </div>
                                    <div className="w-full md:w-1/3 px-3">
                                        <label className={labelClasses}>Offset</label>
                                        <input className={inputClasses} type="number" placeholder="0" value={globalConfig.offset} onChange={onOffsetChange} />
                                    </div>
                                    <div className="w-full md:w-1/3 px-3">
                                        <label className={labelClasses}>Fov</label>
                                        <input className={inputClasses} type="number" placeholder="0" value={globalConfig.fov} onChange={onFovChange} />
                                    </div>
                                </div>
                                <div className="flex flex-wrap -mx-3 mb-4">
                                    <div className="w-full md:w-1/2 px-3">
                                        <label className={labelClasses}>Onload portal</label>
                                        <SelectDropdown
                                            value={globalConfig.onloadPortal}
                                            options={mappedVftPortals}
                                            onSelectChange={onOnloadPortalChange} />
                                    </div>
                                    <div className="w-full md:w-1/2 px-3">
                                        <label className={labelClasses}>Auto rotate</label>
                                        <div className='text-left'>
                                            <ToggleButton isChecked={globalConfig.autoRotate} onToggle={onAutoRotateChange} />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap -mx-3 mb-4">
                                    <div className="w-full md:w-1/2 px-3">
                                        <label className={labelClasses}>Canvas file</label>
                                        <SelectDropdown
                                            value={globalConfig.canvasSrc}
                                            options={htmlPaths}
                                            onSelectChange={onCanvasSrcChange} />
                                    </div>
                                    <div className="w-full md:w-1/2 px-3">
                                        <label className={labelClasses}>Canvas display mode</label>
                                        <SelectDropdown
                                            value={globalConfig.canvasDisplayMode}
                                            options={canvasDisplayModes}
                                            onSelectChange={onCanvasDisplayChange} />
                                    </div>
                                </div>
                                {
                                        globalConfig.canvasDisplayMode === "overlay" &&
                                        <div className="flex flex-wrap -mx-3 mb-4">
                                            <div className="w-full md:w-1/3 px-3">
                                                <label className={labelClasses}>Overlay width</label>
                                                <input className={inputClasses} type="number" placeholder="300" value={globalConfig.canvasOverlayWidth} onChange={onCanvasOverlayWidthChange} />
                                            </div>
                                            <div className="w-full md:w-1/3 px-3">
                                                <label className={labelClasses}>Overlay height</label>
                                                <input className={inputClasses} type="number" placeholder="300" value={globalConfig.canvasOverlayHeight} onChange={onCanvasOverlayHeightChange} />
                                            </div>
                                            <div className="w-full md:w-1/3 px-3">
                                                <label className={labelClasses}>Overlay background</label>
                                                <SelectDropdown
                                                    value={globalConfig.canvasOverlayBackground}
                                                    options={canvasOverlayBackgrounds}
                                                    onSelectChange={onCanvasOverlayBackgroundChange} />
                                            </div>
                                        </div>
                                    }
                            </Tabs.Content>

                            <Tabs.Content value="scene">
                                <div className='my-3'>
                                    <NodeEditorHeader
                                        typeLabel='scenes'
                                        childNodeLength={globalConfig.scenes.length}
                                        onChildNodeAdd={onSceneAdd} />

                                    {
                                        globalConfig.scenes.map((item, index) => (
                                            <PanoramaVFTSceneEditor
                                                key={index}
                                                nodeKey={index}
                                                scene={item}
                                                updateScene={onSceneChange}
                                                onChevronUp={onSceneEditorUp}
                                                onChevronDown={onSceneEditorDown}
                                                onDelete={onSceneEditorDelete} />
                                        ))
                                    }
                                </div>
                            </Tabs.Content>

                            <Tabs.Content value="portal">
                                <div className='my-3'>
                                    <NodeEditorHeader
                                        typeLabel='portals'
                                        childNodeLength={globalConfig.vftPortals.length}
                                        onChildNodeAdd={onPortalAdd} />

                                    {
                                        globalConfig.vftPortals.map((item, index) => (
                                            <PanoramaVFTPortalEditor
                                                key={index}
                                                nodeKey={index}
                                                portal={item}
                                                availablePortals={mappedVftPortals}
                                                availableScenes={mappedScenes}
                                                updatePortal={onPortalChange}
                                                onChevronUp={onPortalEditorUp}
                                                onChevronDown={onPortalEditorDown}
                                                onDelete={onPortalEditorDelete} />
                                        ))
                                    }
                                </div>
                            </Tabs.Content>

                            <Tabs.Content value="infospot">
                                <div className='my-3'>
                                    <NodeEditorHeader
                                        typeLabel='portals'
                                        childNodeLength={globalConfig.infoSpots.length}
                                        onChildNodeAdd={onInfospotEditorAdd} />

                                    {
                                        globalConfig.infoSpots.map((item, index) => (
                                            <PanoramaVFTInfospotEditor
                                                key={index}
                                                nodeKey={index}
                                                infospot={item}
                                                availablePortals={mappedVftPortals}
                                                availableScenes={mappedScenes}
                                                updateInfospot={onInfospotChange}
                                                onChevronUp={onInfospotEditorUp}
                                                onChevronDown={onInfospotEditorDown}
                                                onDelete={onInfospotEditorDelete} />
                                        ))
                                    }
                                </div>
                            </Tabs.Content>
                        </div>
                    </Tabs.Root>
                </form>
            </div>
        </div>
    );
}

export default PanoramaVFTEditor;